import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classnames from 'classnames/bind';

import { operations as eventRecordsOperations } from '~/state/eventRecords';

import StyledComponent from '~/components/Styled/Styled';
import Layout from '~/components/Layout/Layout';
import MetaTags from '~/components/MetaTags/MetaTags';
import TodaysAlertsTooltip from '~/components/TodaysAlertsTooltip/TodaysAlertsTooltip';
import ResultsEventCard from '~/components/ResultsEventCard/ResultsEventCard';
import ResultsMatchupCard from '~/components/ResultsMatchupCard/ResultsMatchupCard';

import getOr from '~/util/getOr';

import ResultsMap from '~/constants/ResultsMap';

import css from './Results.scss';

const cssContext = classnames.bind(css);

const eventStatusOrder = ['in_progress', 'pre_game', 'final'];

type Props = {
  pageData: any;
  eventRecords: any;
  leagueSlug: string;
  setEventRecords: any;
};

type State = {
  matchupEvent: any;
  selectedDate: any;
  openTooltip: boolean;
};

class Results extends React.Component<Props, State> {
  static displayName = 'Results';

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.pageData.selectedDate === prevState.selectedDate) {
      return null;
    }
    return {
      matchupEvent: null,
      selectedDate: nextProps.pageData.selectedDate,
      openTooltip: false,
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      matchupEvent: null,
      selectedDate: null,
      openTooltip: false,
    };
  }

  onEventClicked(event) {
    this.getEventRecords(event.id);
    this.setState({ matchupEvent: event });
  }

  getEventRecords(id) {
    const eventNavElements = getOr(
      ResultsMap[this.props.leagueSlug],
      'matchupCards.eventElements',
      []
    );
    eventNavElements.forEach(navElement =>
      this.props.setEventRecords(this.props.leagueSlug, id, navElement.property)
    );
  }

  renderGetTodaysAlerts() {
    return (
      <button
        className={css.getTodaysAlerts}
        onClick={() => this.setState({ openTooltip: true })}
        onBlur={e => {
          const { currentTarget } = e;
          setTimeout(() => {
            if (!currentTarget.contains(document.activeElement)) {
              this.setState({ openTooltip: false });
            }
          }, 0);
        }}
      >
        Get Today&#39;s Alerts
        {this.state.openTooltip && (
          <div className={css.getTodaysAlertsTooltip}>
            <TodaysAlertsTooltip
              closeClick={() => setTimeout(() => this.setState({ openTooltip: false }), 0)}
              leagueSlug={this.props.leagueSlug}
            />
          </div>
        )}
      </button>
    );
  }

  render() {
    /* prettier-ignore */
    const { eventRecords, pageData: { events, schedule, selectedDate } } = this.props;
    const orderedEvents = eventStatusOrder
      .map(status => events.filter(event => event.event_status === status))
      .reduce((acc, val) => acc.concat(val), []);
    const currentSeasons = schedule.current_season || schedule.groups;
    const currentScheduleItem = currentSeasons.find(item => item.id === selectedDate);
    const eventSelected = !!this.state.matchupEvent;
    const resultEventContainerClasses = cssContext(css.resultsEventCardContainer, {
      desktopView: eventSelected,
    });

    const scheduleTimeLabelClasses = cssContext(css.scheduleTimeLabel, {
      desktopView: eventSelected,
    });

    return (
      <Layout showEventsNav hideEventsNavOnMobile={eventSelected}>
        <MetaTags content={this.props.pageData.currentLeague} type="events" />
        <div className={scheduleTimeLabelClasses}>{getOr(currentScheduleItem, 'label', '')}</div>
        <div className={css.resultEvents}>
          {!eventSelected && this.renderGetTodaysAlerts()}

          <div id="eventResults" className={resultEventContainerClasses}>
            {orderedEvents.map((event, i) => {
              const resultEventCardClasses = cssContext(css.resultsEventCard, {
                seperator: i,
                resultEventCardSelected: Object.is(event, this.state.matchupEvent),
              });

              return (
                <div key={`card${event.id}`}>
                  <div
                    className={`${css.desktopView} ${resultEventCardClasses}`}
                    onClick={() => this.onEventClicked(event)}
                  >
                    <ResultsEventCard event={event} />
                  </div>
                  <div className={`${css.mobileView} ${resultEventCardClasses}`}>
                    <a href={event.api_uri}>
                      <ResultsEventCard event={event} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>

          {eventSelected && (
            <div className={css.resultsMatchupCardContainer}>
              <ResultsMatchupCard event={this.state.matchupEvent} eventRecords={eventRecords} />
            </div>
          )}
        </div>
      </Layout>
    );
  }
}

const mapStateToProps = state => ({
  eventRecords: state.eventRecordsData.cache,
});

const mapDispatchToProps = dispatch => ({
  setEventRecords: bindActionCreators(eventRecordsOperations.setEventRecords, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(StyledComponent(Results, [css]));
